import React, { useState } from 'react';
import { motion, useAnimation, AnimatePresence } from 'motion/react';
import { COLORS, Icons } from '../constants';

const PRIZES = [
  { label: 'GOLD SPONSOR', color: '#D4AF37', value: 'gold' },
  { label: 'SOCIAL SHOUTOUT', color: '#5FA136', value: 'social' },
  { label: 'GT MATCHES 50%', color: '#376E20', value: 'match_50' },
  { label: 'HERO BADGE', color: '#4D2809', value: 'hero' },
  { label: 'SILVER SPONSOR', color: '#A8D672', value: 'silver' },
  { label: 'EPIC KARMA', color: '#8E9299', value: 'karma' },
];

const PROJECTS = [
  { id: 1, title: 'Hazardous Oak over Veteran\'s Roof', goal: '$2,500', raised: '$1,800', progress: 72, applicant: 'Mr. Henderson (82)' },
  { id: 2, title: 'Storm Cleanup for Local School', goal: '40 Labor Hrs', raised: '25 Hrs', progress: 62, applicant: 'Columbus Elementary' },
  { id: 3, title: 'Dead Pine Removal - Single Mother', goal: '$1,200', raised: '$300', progress: 25, applicant: 'Sarah Jenkins' },
];

const LEADERBOARD = [
  { rank: 1, name: 'TimberTech Pros', contribution: '80 Hrs / $2k', badge: '🏆' },
  { rank: 2, name: 'O\'Reilly Tree Care', contribution: '45 Hrs / $1k', badge: '🥈' },
  { rank: 3, name: 'GreenTree Arboriculture', contribution: 'Matching Sponsor', badge: '⭐' },
  { rank: 4, name: 'Dave\'s Stump Grinding', contribution: '20 Hrs', badge: '🏅' },
];

const PayItForwardView: React.FC = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const controls = useAnimation();

  const handlePledge = (projectId: number) => {
    setSelectedProject(projectId);
    setShowPledgeModal(true);
  };

  const submitPledge = () => {
    setShowPledgeModal(false);
    spinWheel();
  };

  const spinWheel = async () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setResult(null);

    const extraSpins = 5 + Math.floor(Math.random() * 5);
    const randomOffset = Math.floor(Math.random() * 360);
    const totalRotation = rotation + (extraSpins * 360) + randomOffset;
    
    setRotation(totalRotation);

    await controls.start({
      rotate: totalRotation,
      transition: { duration: 4, ease: [0.15, 0, 0.15, 1] }
    });

    setIsSpinning(false);
    
    const normalizedRotation = totalRotation % 360;
    const segmentSize = 360 / PRIZES.length;
    const prizeIndex = (PRIZES.length - Math.floor(normalizedRotation / segmentSize)) % PRIZES.length;
    setResult(PRIZES[prizeIndex].label);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="bg-bark p-10 rounded-[40px] text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-xl">
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center text-red-400">
              <Icons.Heart />
            </div>
            <h2 className="text-3xl font-black tracking-tight">Pay It Forward</h2>
          </div>
          <p className="text-stone-300 font-medium text-lg leading-relaxed">
            Community taking care of each other. Pledge equipment, labor, or funds to help locals in need with dangerous trees they can't afford to remove. Every pledge earns a spin on the Karma Wheel for sponsor perks!
          </p>
        </div>
        <div className="absolute right-0 top-0 opacity-10 transform translate-x-10 -translate-y-10 scale-150 pointer-events-none text-white">
           <Icons.Tree />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: The Wheel */}
        <div className="lg:col-span-5 bg-white p-8 rounded-[40px] border border-stone-200 shadow-sm flex flex-col items-center justify-center min-h-[600px]">
          <div className="text-center space-y-2 mb-12">
            <h3 className="text-2xl font-black text-palm tracking-tight uppercase">The Karma Wheel</h3>
            <p className="text-stone-500 text-sm font-medium">
              Pledge to a project to spin for sponsor badges, social shoutouts, and matching funds!
            </p>
          </div>

          <div className="relative w-72 h-72 md:w-80 md:h-80 mb-12">
            {/* Pointer */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 w-8 h-10 bg-red-500 clip-path-triangle shadow-lg border-2 border-white" 
                 style={{ clipPath: 'polygon(50% 100%, 0 0, 100% 0)' }}>
            </div>

            {/* The Wheel */}
            <motion.div
              animate={controls}
              className="w-full h-full rounded-full border-8 border-bark shadow-2xl relative overflow-hidden bg-white"
              style={{ transformOrigin: 'center center' }}
            >
              {PRIZES.map((prize, index) => {
                const angle = (360 / PRIZES.length) * index;
                return (
                  <div
                    key={index}
                    className="absolute top-0 left-1/2 w-1/2 h-full origin-left"
                    style={{
                      transform: `rotate(${angle}deg)`,
                      backgroundColor: prize.color,
                      clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 50%)',
                    }}
                  >
                    <div 
                      className="absolute top-10 left-6 text-white font-black text-[10px] md:text-xs whitespace-nowrap transform -rotate-90 origin-left tracking-widest"
                      style={{ transform: 'rotate(30deg) translate(40px, 0)' }}
                    >
                      {prize.label}
                    </div>
                  </div>
                );
              })}
              
              {/* Center Hub */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full border-4 border-bark z-10 flex items-center justify-center shadow-inner">
                <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
              </div>
            </motion.div>
          </div>

          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                className="bg-stone-50 w-full p-6 rounded-3xl border-2 border-gecko text-center"
              >
                <p className="text-stone-400 font-bold uppercase tracking-widest text-[10px] mb-1">Reward Unlocked!</p>
                <h4 className="text-2xl font-black text-palm">{result}</h4>
                <p className="text-stone-500 text-xs mt-2 font-medium">Thank you for your incredible generosity.</p>
              </motion.div>
            ) : (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <p className="text-stone-400 font-bold uppercase tracking-widest text-xs">Awaiting Pledge...</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Column: Projects & Leaderboard */}
        <div className="lg:col-span-7 space-y-8">
          
          {/* Active Projects */}
          <div className="bg-white p-8 rounded-[40px] border border-stone-200 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-bark">Active Relief Projects</h3>
              <span className="bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">Urgent Need</span>
            </div>
            
            <div className="space-y-4">
              {PROJECTS.map(project => (
                <div key={project.id} className="p-6 bg-stone-50 rounded-3xl border border-stone-100 hover:border-palm/30 transition-colors group">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-bold text-lg text-bark">{project.title}</h4>
                      <p className="text-sm text-stone-500 mt-1">Applicant: {project.applicant}</p>
                    </div>
                    <button 
                      onClick={() => handlePledge(project.id)}
                      className="px-6 py-2 bg-palm text-white text-sm font-bold rounded-xl hover:bg-gecko transition shadow-lg shadow-palm/20 transform active:scale-95"
                    >
                      Pledge
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs font-bold text-stone-500 uppercase tracking-widest">
                      <span>Raised: {project.raised}</span>
                      <span>Goal: {project.goal}</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className="bg-gecko h-2.5 rounded-full transition-all duration-1000" 
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Leaderboard */}
          <div className="bg-white p-8 rounded-[40px] border border-stone-200 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <span className="text-2xl">🏆</span>
              <h3 className="text-2xl font-black text-bark">Local Heroes Leaderboard</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest rounded-l-2xl">Rank</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Hero / Company</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right rounded-r-2xl">Contribution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {LEADERBOARD.map((hero) => (
                    <tr key={hero.rank} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="font-black text-stone-400">#{hero.rank}</span>
                          <span className="text-xl">{hero.badge}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-bark">{hero.name}</td>
                      <td className="px-6 py-4 font-black text-gecko text-right">{hero.contribution}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* Pledge Modal */}
      <AnimatePresence>
        {showPledgeModal && (
          <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[150] p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl p-8"
            >
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-black text-bark">Make a Pledge</h3>
                <button onClick={() => setShowPledgeModal(false)} className="text-stone-400 hover:text-bark bg-stone-100 p-2 rounded-full transition">
                  <Icons.Close />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Pledge Type</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button className="py-3 border-2 border-palm bg-palm/5 text-palm font-bold rounded-xl text-sm">Funds</button>
                    <button className="py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl text-sm hover:border-stone-200">Labor</button>
                    <button className="py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl text-sm hover:border-stone-200">Equipment</button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-bold">$</span>
                    <input 
                      type="number" 
                      placeholder="0.00" 
                      className="w-full pl-8 p-4 bg-stone-50 rounded-2xl border-none focus:ring-2 focus:ring-palm font-bold text-bark text-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Message of Support (Optional)</label>
                  <textarea 
                    rows={3}
                    placeholder="We've got your back!"
                    className="w-full p-4 bg-stone-50 rounded-2xl border-none focus:ring-2 focus:ring-palm text-sm text-bark resize-none"
                  />
                </div>

                <div className="pt-4">
                  <button 
                    onClick={submitPledge}
                    className="w-full py-4 bg-palm text-white rounded-2xl font-black text-lg uppercase tracking-widest hover:bg-gecko transition shadow-xl shadow-palm/20"
                  >
                    Confirm & Spin Wheel
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PayItForwardView;
